import { ApolloClient } from '@apollo/client';
import { UserContextInterface } from 'src/context/user';
import { FunnelCart } from 'src/ts/types/cart';
import {
  ADD_CODE_TO_CART,
  REMOVE_CODE_FROM_CART,
  UPDATE_CART,
  UPDATE_CART_WITH_PREVIEW_DISCOUNT,
} from 'src/graphql-operations/cart-order-service/carts.mutation';
import {
  AddCodeToCartMutation,
  AddCodeToCartMutationVariables,
  RemoveCodeFromCartMutation,
  RemoveCodeFromCartMutationVariables,
  UpdateCartMutation,
  UpdateCartMutationVariables,
  UpdateCartWithPreviewDiscountMutation,
  UpdateCartWithPreviewDiscountMutationVariables,
} from 'src/ts/@types/generated/graphql/cart-order-service/graphql';
import { ClientNames } from 'src/utils/apolloClient';
import { getDiscountCodeClientSide } from 'src/services/rails-service';

type SetDiscountResult =
  { success: true; cart: FunnelCart } | { success: false; error: string };

/**
 * The application of the discount, retrieval of the cartLedger, and possible return of error states
 * is somewhat complicated. The different code paths hinge on whether or not the user is logged in.
 *
 * If the user is NOT logged in:
 *  - Apply the discountCode to `funnelParams`, this is just running `updateCart`. There is zero
 *    validation done.
 *  - Pull the cartLedger using previewDiscount with the provided discountCode
 *  - The only error state that should be returned is an invalid discountCode
 *
 * If the user IS logged in:
 *  - Apply the discountCode via the `addCodeToCart` mutation. This mutation handles validation for us
 *  - Pull the cartLedger using the regular non-preview cartLedger field resolver
 *  - Error states should include all discount validations (ineligible, invalid discount, etc)
 */
export async function setDiscountOnCart(input: {
  cart: FunnelCart;
  discountCode: string;
  apolloClient: ApolloClient;
  userContext: UserContextInterface;
}): Promise<SetDiscountResult> {
  const { userContext, ...rest } = input;
  const isLoggedIn = !!userContext.user;

  if (isLoggedIn) {
    return setDiscountOnCartForLoggedInUser(rest);
  } else {
    return setDiscountOnCartForAnonymousUser({ ...rest, userContext });
  }
}

/**
 * For logged in users: Apply discount via addCodeToCart mutation.
 * This mutation handles full validation (eligibility, invalid discount, etc).
 * The cartLedger is returned using the regular non-preview field resolver.
 */
async function setDiscountOnCartForLoggedInUser(input: {
  cart: FunnelCart;
  discountCode: string;
  apolloClient: ApolloClient;
}): Promise<SetDiscountResult> {
  const { cart, discountCode, apolloClient } = input;

  try {
    const { data } = await apolloClient.mutate<
      AddCodeToCartMutation,
      AddCodeToCartMutationVariables
    >({
      mutation: ADD_CODE_TO_CART,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      variables: {
        code: discountCode.trim().toUpperCase(),
        uuid: cart.uuid,
      },
    });
    if (data?.addCodeToCart?.__typename === 'Cart') {
      const { discountErrors } = data.addCodeToCart;

      // discountErrors contains validation errors like ineligibility
      if (discountErrors?.length) {
        return { success: false, error: discountErrors[0] };
      }

      return { success: true, cart: data.addCodeToCart as FunnelCart };
    } else {
      return { success: false, error: 'Failed to apply promo code' };
    }
  } catch {
    return { success: false, error: 'Failed to apply promo code' };
  }
}

/**
 * For non-logged in users: Store discount code in funnelData.params via updateCart.
 * Validates that the discount code exists before storing it.
 * The cartLedger should be pulled using previewDiscount with the provided discountCode.
 * The only error state is an invalid discountCode.
 */
async function setDiscountOnCartForAnonymousUser(input: {
  cart: FunnelCart;
  discountCode: string;
  apolloClient: ApolloClient;
  userContext: UserContextInterface;
}): Promise<SetDiscountResult> {
  const { cart, discountCode, apolloClient, userContext } = input;
  const normalizedCode = discountCode.trim().toUpperCase();

  // Validate that the discount code exists before storing it
  try {
    await getDiscountCodeClientSide({
      discountCode: normalizedCode,
      tenantId: userContext.tenantId,
    });
  } catch {
    return { success: false, error: 'Invalid promo code' };
  }

  try {
    const { data } = await apolloClient.mutate<
      UpdateCartWithPreviewDiscountMutation,
      UpdateCartWithPreviewDiscountMutationVariables
    >({
      mutation: UPDATE_CART_WITH_PREVIEW_DISCOUNT,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      variables: {
        uuid: cart.uuid,
        funnelData: {
          params: {
            discountCode: normalizedCode,
          },
        },
        previewFunnelDiscountCode: true,
      },
    });

    if (data?.updateCart?.__typename === 'Cart') {
      return { success: true, cart: data.updateCart as FunnelCart };
    }

    return { success: false, error: 'Failed to apply promo code' };
  } catch {
    return { success: false, error: 'Failed to apply promo code' };
  }
}

type RemoveDiscountResult =
  { success: true; cart: FunnelCart } | { success: false; error: string };

/**
 * Entry point for removing a discount code from a cart. This is a much simpler flow than the adding
 * of a discount code.
 *
 * If you're logged in:
 *  - Call the RemoveCodeFromCartMutation
 *  - Use the regular non-preview cartLedger query
 *
 * If you're not logged in:
 *  - Call the updateCart and remove the discount from the funnelParams
 *  - Use the regular non-preview cartLedger query
 */
export async function removeDiscountFromCart(input: {
  cart: FunnelCart;
  apolloClient: ApolloClient;
  userContext: UserContextInterface;
}): Promise<RemoveDiscountResult> {
  const { userContext, ...rest } = input;
  const isLoggedIn = !!userContext.user;

  if (isLoggedIn) {
    return removeDiscountFromCartForLoggedInUser(rest);
  } else {
    return removeDiscountFromCartForAnonymousUser(rest);
  }
}

/**
 * For logged in users: Remove discount via removeCodeFromCart mutation.
 * The cartLedger is returned using the regular non-preview field resolver.
 */
async function removeDiscountFromCartForLoggedInUser(input: {
  cart: FunnelCart;
  apolloClient: ApolloClient;
}): Promise<RemoveDiscountResult> {
  const { cart, apolloClient } = input;

  try {
    const { data } = await apolloClient.mutate<
      RemoveCodeFromCartMutation,
      RemoveCodeFromCartMutationVariables
    >({
      mutation: REMOVE_CODE_FROM_CART,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      variables: {
        uuid: cart.uuid,
      },
    });

    if (data?.removeCodeFromCart?.__typename === 'Cart') {
      return { success: true, cart: data.removeCodeFromCart as FunnelCart };
    }

    return { success: false, error: 'Failed to remove promo code' };
  } catch {
    return { success: false, error: 'Failed to remove promo code' };
  }
}

/**
 * For non-logged in users: Remove discount code from funnelData.params via updateCart.
 * The cartLedger is returned using the regular non-preview field resolver.
 */
async function removeDiscountFromCartForAnonymousUser(input: {
  cart: FunnelCart;
  apolloClient: ApolloClient;
}): Promise<RemoveDiscountResult> {
  const { cart, apolloClient } = input;

  try {
    const { data } = await apolloClient.mutate<
      UpdateCartMutation,
      UpdateCartMutationVariables
    >({
      mutation: UPDATE_CART,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      variables: {
        uuid: cart.uuid,
        funnelData: {
          params: {
            discountCode: null,
          },
        },
      },
    });

    if (data?.updateCart?.__typename === 'Cart') {
      return { success: true, cart: data.updateCart as FunnelCart };
    }

    return { success: false, error: 'Failed to remove promo code' };
  } catch {
    return { success: false, error: 'Failed to remove promo code' };
  }
}
